import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { AuthProvider } from '@formic/auth';
import { ClientProvider, createClient } from '@formic/fusion-graphql';
import React, { FC, Suspense } from 'react';
import { BrowserRouter } from 'react-router-dom';

import AppRoutes from './App.routes';
import { getAuthService } from './authService';
import { isAuthenticationError, redirectToLogin } from './authSession';
import { bffRoutes, formicRequestHeaders } from './bffRoutes';
import postRespondentUpdate from './graphql/postRespondentUpdate';
import MainLayout from './layout/MainLayout';
import LoadingScreen from './LoadingScreen';
import { WebPluginProvider } from './plugins';

const cache = createCache({
  key: 'my-prefix-key',
  stylisPlugins: [],
});

const client = createClient({
  url: bffRoutes.graphql,
  requestHeaders: { ...formicRequestHeaders },
  tagNameExchange: true,
  updates: {
    Mutation: {
      ...postRespondentUpdate,
    },
  },
  getToken: async () => {
    try {
      const auth = await getAuthService();
      return await auth.getToken();
    } catch (error) {
      if (isAuthenticationError(error)) {
        await redirectToLogin();
        return undefined;
      }

      throw error;
    }
  },
});

const App: FC = () => {
  return (
    <React.StrictMode>
      <CacheProvider value={cache}>
        <Suspense fallback={<LoadingScreen />}>
          <AuthProvider service={getAuthService}>
            <ClientProvider client={client}>
              <BrowserRouter>
                <WebPluginProvider>
                  <MainLayout>
                    <AppRoutes />
                  </MainLayout>
                </WebPluginProvider>
              </BrowserRouter>
            </ClientProvider>
          </AuthProvider>
        </Suspense>
      </CacheProvider>
    </React.StrictMode>
  );
};

export default App;
